import React, { Component } from 'react';

import Tracer from '../../utils/tracer';
import getGlobalValue from '../../utils/get-global-value';
import EVENTS from '../../constants/event-bus-events';
import FeatureRollout from '../../utils/feature-rollout';
import { NOOP } from '../../constants';
import { isAllowedToLoadAppName } from '../../utils';
import {
  GRAVITY_ACTIVATION_CLASS,
  GRAVITY_DESIGN_APPS,
  WSB_MERCHANT_SOURCE,
} from '../../shared/constants/merchant';

const moment = getGlobalValue('moment');

function isAppLoaderDeprecatedFor(appName) {
  // This function basically means that the given micro app is now rendered by MicroApp component, in accordance to the new app-shell architecture design. For more info, check this ADR: https://www.notion.so/shorewiki/App-Shell-architecture-redesign-a842d1054de44867a9fa9a1228dab64d

  return !FeatureRollout.isActive('services-app-refactor')
    ? ['subscription'].includes(appName)
    : ['services', 'subscription'].includes(appName);
}

const eventuallyDestroyInstance = async (instancePromise) => {
  if (!instancePromise) return;
  const instance = await instancePromise;
  if (!instance) return;
  Tracer.clearAppConfig();
  instance.destroy();
};

const isAllowedToLoad = (isFreeProduct, name) =>
  isFreeProduct ? isAllowedToLoadAppName(name) : true;

// Defer Gravity app loading until `settings.source` is defined to ensure `.gv-activated`
// styling is applied before the first paint. Non-Gravity apps are unaffected.
const isAwaitingActivation = (settings, name) =>
  GRAVITY_DESIGN_APPS.includes(name) && settings?.source === undefined;

class AppLoader extends Component {
  constructor(props) {
    super(props);

    this.baseRef = React.createRef();
  }

  componentDidMount() {
    this.startAppIfReady(this.props);
  }

  startAppIfReady(props) {
    const { settings, name } = props;
    if (isAwaitingActivation(settings, name)) return;
    if (isAllowedToLoad(settings.isFreeProduct, name)) {
      this.setAppPromise(this.loadAndCreateApp(props), props.name);
    }
  }

  getComputedClassName(props) {
    let className = props.class || '';

    const settingsSource = props.settings?.source;
    const appName = props.name;

    if (
      settingsSource === WSB_MERCHANT_SOURCE &&
      GRAVITY_DESIGN_APPS.includes(appName)
    ) {
      className = className
        ? `${className} ${GRAVITY_ACTIVATION_CLASS}`
        : GRAVITY_ACTIVATION_CLASS;
    }

    return className;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Prop update logic:
    // 1. App switch (`name`/`disabled` changed) -> Destroy old, start new.
    // 2. Route change (only `pathAndQuery` changed) -> Forward to running app.
    // 3. Deferred start (no app running yet) -> Try loading now that props are ready.
    const { name, pathAndQuery, disabled } = this.props;
    const {
      name: nextName,
      disabled: nextDisabled,
      settings: nextSettings,
      pathAndQuery: nextPathAndQuery,
    } = nextProps;
    if (name !== nextName || disabled !== nextDisabled) {
      if (isAllowedToLoad(nextSettings.isFreeProduct, nextName)) {
        eventuallyDestroyInstance(this.appPromise);
        this.appPromise = null;
        this.startAppIfReady(nextProps);
      }
    } else if (name === nextName && pathAndQuery !== nextPathAndQuery) {
      // If `appPromise` is undefined (awaiting activation), `loadAndCreateApp` will pick up the latest `pathAndQuery` later.
      this.appPromise?.then((instance) => {
        if (instance && 'dispatchAction' in instance) {
          instance.dispatchAction({
            type: 'ROUTE_CHANGE',
            payload: nextPathAndQuery,
          });
        }
      });
    } else if (!this.appPromise) {
      this.startAppIfReady(nextProps);
    }

    // set class directly in DOM if changed
    const currentClassName = this.getComputedClassName(this.props);
    const nextClassName = this.getComputedClassName(nextProps);
    if (currentClassName !== nextClassName) {
      this.baseRef.current.className = nextClassName;
    }
  }

  // we don't want rerender here and manually take care of updating the DOM,
  // see componentWillReceiveProps
  shouldComponentUpdate() {
    return false;
  }

  setAppPromise(promise, appName) {
    const loadingStart = Date.now();

    this.appPromise = promise;

    function trackLoadingTime() {
      Tracer.addAction(`${appName}.micro_app.loaded`, {
        microApp: { loadingTimeInMs: Date.now() - loadingStart },
      });
    }

    if (appName) {
      promise.then(this.props.onInstance).then(trackLoadingTime);
    } else {
      promise.then(this.props.onInstance);
    }
  }

  async loadAndCreateApp({
    name,
    eventBus,
    disabled = false,
    onLoadingApp = NOOP,
    onAppAction = NOOP,
    onError = NOOP,
    onDocumentOutdated = NOOP,
    settings = {},
    pathAndQuery = '',
    useHashHistory = false,
    routingType = 'path',
    modal = {},
    loadApp = NOOP,
    documentDate = null,
    trackMethod = NOOP,
    apiClient = {},
    errorLogger = {},
  }) {
    if (!name || disabled || isAppLoaderDeprecatedFor(name)) return null;

    const logError = (error, extraTagsFromMicroApp = {}) => {
      errorLogger.log(
        error instanceof Error ? error : new Error(error),
        {
          tags: {
            app: name,
          },
        },
        extraTagsFromMicroApp
      );
    };

    onLoadingApp(true);
    try {
      this.props.eventBus.emit(EVENTS.CLOSE_MENU);

      const { create, manifest } = await loadApp({
        name,
        apiClient,
        trackMethod,
        settings,
        pathAndQuery,
        useHashHistory,
        routingType,
        modal,
        eventBus: {
          on: eventBus.on,
          off: eventBus.off,
          emit: (eventName, payload = {}) => {
            eventBus.emit(eventName, {
              payload,
              metadata: { appName: name, event: eventName },
            });
          },
        },
        onAction: (action) => {
          onAppAction(name, action);
        },
        container: this.baseRef.current,
        logError,
      });

      Tracer.setAppName(name);

      const appYoungerThanDocument =
        !!documentDate && moment(manifest.lastModified) > moment(documentDate);

      // prevent app creation if app is younger than document
      if (appYoungerThanDocument) {
        onDocumentOutdated();
        return null;
      }

      try {
        return create();
      } catch (createError) {
        onError(createError, { stage: 'create', app: name });
        return null;
      }
    } catch (error) {
      onError(error, { stage: 'load', app: name });
      return null;
    } finally {
      onLoadingApp(false);
    }
  }

  render() {
    return (
      <div
        ref={this.baseRef}
        className={this.getComputedClassName(this.props)}
      />
    );
  }
}

export default AppLoader;
